import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Typography,
} from "@mui/material";
import useUpdate from "../../../api/useUpdate";
import { useTranslation } from "react-i18next";
import { TAB_KEYS, TAB_KEY_TO_LABEL } from "../../constants/tabKeys";

const UserEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, resetForm } = useUpdate("admin/users", token);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isStaff, setIsStaff] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [allowedTabs, setAllowedTabs] = useState([]);

  const toggleTab = (tabKey) => {
    setAllowedTabs((prev) =>
      prev.includes(tabKey)
        ? prev.filter((k) => k !== tabKey)
        : [...prev, tabKey]
    );
  };

  useEffect(() => {
    if (editData) {
      setUsername(editData.username || "");
      setEmail(editData.email || "");
      setFirstName(editData.first_name || "");
      setLastName(editData.last_name || "");
      setPhoneNumber(editData.phone_number || "");
      setIsStaff(editData.is_staff || false);
      setIsSuperuser(editData.is_superuser || false);
      setAllowedTabs(
        Array.isArray(editData.allowed_tabs) ? [...editData.allowed_tabs] : []
      );
    }
  }, [editData]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("email", email);
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("phone_number", phoneNumber);
    formData.append("is_staff", isStaff || isSuperuser);
    formData.append("is_superuser", isSuperuser);
    formData.append(
      "allowed_tabs",
      JSON.stringify(isSuperuser ? [] : allowedTabs)
    );
    handleUpdate(editData.id, formData);
  };

  const canSubmit = username && (isSuperuser || allowedTabs.length > 0);

  useEffect(() => {
    if (resetForm) {
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("user.editUser")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            label={t("user.username")}
            fullWidth
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.email")}
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.firstName")}
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.lastName")}
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.phoneNumber")}
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("user.accountType")}
            fullWidth
            value={isStaff}
            onChange={(e) => setIsStaff(e.target.value)}
          >
            <MenuItem value={true}>{t("user.staff")}</MenuItem>
          </TextField>

          {(isStaff || isSuperuser) && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSuperuser}
                    onChange={(e) => {
                      setIsSuperuser(e.target.checked);
                      if (e.target.checked) setAllowedTabs([]);
                    }}
                  />
                }
                label={t("user.superAdmin", "Super Admin (full access)")}
                sx={{ mt: 1, display: "block" }}
              />
              {!isSuperuser && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t(
                      "user.tabPermissions",
                      "Tab permissions (select tabs this user can access)"
                    )}
                  </Typography>
                  <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap" }}>
                    {TAB_KEYS.map((tabKey) => (
                      <FormControlLabel
                        key={tabKey}
                        control={
                          <Checkbox
                            checked={allowedTabs.includes(tabKey)}
                            onChange={() => toggleTab(tabKey)}
                          />
                        }
                        label={t(TAB_KEY_TO_LABEL[tabKey] || tabKey)}
                        sx={{ width: "50%", minWidth: 140 }}
                      />
                    ))}
                  </FormGroup>
                  {allowedTabs.length === 0 && (
                    <Typography variant="caption" color="error">
                      {t(
                        "user.selectAtLeastOneTab",
                        "Select at least one tab."
                      )}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("user.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !canSubmit}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("user.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEdit;
